import React, { useMemo } from "react";
import ProductCard from "../ProductCard.js";
import { Button } from "wbm-inputs";
import { CartItem, ModalPopup } from "wbm-generic-components";
import { dataTransform, cartItemTransform } from "./transform.js";
import { isYesOrTrue } from "../../utilities/utilities.js";
import ConfigProps from "../../constants/configProps.js";
import { ISupercedeProduct } from "@wbmason/cart-service/interfaces/supercedeProduct.js";
import { IIndicatorIcon } from "../../interfaces/IIndicatorIcon.js";
import { IItemAvailability } from "../../interfaces/IItemAvailability.js";
import { IProductInfo } from "../../interfaces/IProductInfo.js";
import { IProductImage } from "../../interfaces/IProductImage.js";
import { ISupersedeCartItem } from "../../interfaces/ISupersedeCartItem.js";

export interface ISupersedeSoftProps {
  configProps: Record<string, string>;
  supersedeProducts: ISupercedeProduct[];
  supersedeClick: (
    supersedeProduct: ISupercedeProduct[],
    supersedeProducts: IProductInfo
  ) => void;
  onClose?: () => void;
  isLoading?: boolean;
  setShow?: boolean;
  productImages: IProductImage[];
  sourceProductIndicators: IIndicatorIcon[];
  relatedProductIndicators: IIndicatorIcon[];
  sourceProductAvailability: IItemAvailability;
  relatedProductAvailability: IItemAvailability[];
  productImagesLoaded?: boolean;
  hostUrl: string;
}

export const SupercedeSoftModal: React.FC<ISupersedeSoftProps> = (
  props: ISupersedeSoftProps
) => {
  const displayCloseButtonOnSupercedesPopup = isYesOrTrue(
    props.configProps[ConfigProps.DisplayCloseButtonOnSupercedesPopup]
  );

  const buttonClick = (productInfo: IProductInfo) => {
    props.supersedeClick(props.supersedeProducts, productInfo);
  };

  const transformedSupersedes = useMemo(() => {
    return props.supersedeProducts.map((supersede) => ({
      ...supersede,
      relatedProducts: supersede.relatedProducts.map((relatedProduct) =>
        dataTransform(relatedProduct, props.productImages)
      ),
    }));
  }, [props.supersedeProducts, props.productImages]);

  const cartItemInfo: ISupersedeCartItem = useMemo(() => {
    return cartItemTransform(
      props.supersedeProducts[0],
      props.sourceProductIndicators,
      props.sourceProductAvailability,
      props.productImages,
      props.hostUrl
    );
  }, [
    props.supersedeProducts,
    props.sourceProductIndicators,
    props.sourceProductAvailability,
    props.productImages,
    props.hostUrl
  ]);

  const getAvailability = useMemo(() => {
    return (product: IProductInfo) => {
      return props.relatedProductAvailability.filter(
        (q) => q.itemId === product.itemId && q.unitId === product.unitId
      )[0];
    };
  }, [props.relatedProductAvailability]);

  return (
    <ModalPopup
      header="YOU HAVE SELECTED THIS ITEM"
      boxShadow
      closeIcon={displayCloseButtonOnSupercedesPopup}
      setShow={props.setShow}
      isLoading={props.isLoading}
      disableBackdropClick
      onClose={props.onClose}
      className={`supercede-soft-modal ${
        props.isLoading ? "content-loading" : ""
      }`}
      showCancelButton={displayCloseButtonOnSupercedesPopup}
      customZIndex={1500}
    >
      <div>
        <div className="row no-gutters cart-item-container">
          <div className="col-12 col-md-6 col-sm-6 product-description">
            <CartItem {...cartItemInfo} />
          </div>
          <div className="col-12 col-md-2 col-sm-2">&nbsp;</div>
          <div className="col-12 col-md-4 col-sm-4 top-wrapper">
            <Button
              className="btn-primary continue-button"
              onClick={() =>
                buttonClick(
                  dataTransform(
                    props.supersedeProducts[0].sourceProduct,
                    props.productImages
                  )
                )
              }
            >
              CONTINUE
            </Button>
          </div>
        </div>
        {transformedSupersedes.map((supersede, i) => (
          <div key={`supersedeItem_${i}`}>
            <div className="also-consider-wrapper">
              <div className="also-consider">
                {supersede.customMessage || "Also consider"}
              </div>
              <div className="row no-gutters">
                {supersede.relatedProducts.map((relatedProduct, index) => {
                  return (
                    <div
                      className="col-12 col-md-6 col-sm-6 spacing soft-supercede-card"
                      key={relatedProduct.itemId + index}
                    >
                      <ProductCard
                        key={"supercede-card" + index}
                        configProps={props.configProps}
                        productInfo={relatedProduct}
                        itemIndicators={props.relatedProductIndicators}
                        itemAvailability={getAvailability(relatedProduct)}
                        maxLength={4}
                        displayAddToCart={false}
                        productImagesLoaded={props.productImagesLoaded}
                        hasMultipleSupersedes
                        supersedeClick={(product) => buttonClick(product)}
                        preventListPrice
                        hideRelevanceScore
                        swapSavings={
                          props.supersedeProducts[0].sourceProduct.sellPrice -
                          relatedProduct.unitPrice
                        }
                        hostUrl={props.hostUrl}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </ModalPopup>
  );
};

export default SupercedeSoftModal;
